import { BasicResponseDTO } from "@dto/basicResponse.dto";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useLatestGet } from "./useLatestRequest";

interface Data<T> extends BasicResponseDTO {
  data: T;
}

/**
 * Declarative GET bound to render state: fetches on mount and whenever `deps` change, aborting the previous fetch
 * @param apiPath GET endpoint
 * @param errorMsg toast text on failure
 * @param deps values whose change triggers a refetch
 * @param initialData state before the first response
 * @param enabled false skips fetching until it turns true
 * @param showError false keeps failures silent
 * @param apiParams query parameters
 * @returns the data, its setter and the loading flag
 */
const useGetApiData = <T, P extends Data<T>>(
  apiPath: string,
  errorMsg: string,
  deps: readonly unknown[],
  initialData?: T | (() => T),
  enabled = true,
  showError = true,
  apiParams?: unknown): [T, Dispatch<SetStateAction<T>>, boolean] => {
  const [data, setData] = useState<T>(initialData);
  const [get, loading, abort] = useLatestGet<P>(enabled);

  // refetch is caller-controlled through deps; apiParams is a fresh object every render
  useEffect(() => {
    if (!enabled) return;
    void get(apiPath, { errorMessage: errorMsg, silent: !showError, config: { params: apiParams } })
      .then(result => { if (result !== undefined) setData(result); });
    // a deps change or disabling drops the in-flight fetch instead of storing its late result
    return abort;
  }, [...deps, enabled, get, abort]);

  return [data, setData, loading];
};

export default useGetApiData;
